import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Renderer2,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  AfterViewInit,
  HostListener,
  OnDestroy, // Import OnDestroy for cleanup
  DebugElement
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';

// --- (Your existing imports) ---
import { ComponentGeneratorService } from '../../../../../content-editor/src/lib/services/component-generator.service';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { environment } from '@environment';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { RappiderHtmlViewerModule, RappiderJsonViewerComponent, RappiderModalModule } from '@rappider/rappider-components';
import { FormsModule } from '@angular/forms';
import { UIComponentDataProviderService } from 'libs/ai-integration/src/lib/services/ui-component-data-provider.service';
import { ComponentControllerService, ComponentDefinitionControllerService } from '@rappider/rappider-sdk';
import * as lodash from 'lodash';
import { NotificationService } from '@rappider/services';
import { RappiderMarkdownViewerModule } from 'libs/components/src/lib/components/markdown-viewer/markdown-viewer.module';
import { RappiderComponentDefinitionInfoFilesComponent } from
  'libs/content-editor/src/lib/components/component-definition-info-files/component-definition-info-files.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';

import { EntityFetcherService } from 'libs/ai-integration/src/lib/services/entity-fetcher.service';
import { UpdateComponentDefinitionSuccessful } from 'libs/component-definition/src/lib/state/component-definition.actions';
import { selectComponentDefinitionById } from 'libs/component-definition/src/lib/state/component-definition.selectors';

// This allows TypeScript to recognize the _flutter object loaded from flutter.js
declare const _flutter: any;

@Component({
  selector: 'rappider-compiled-component-renderer',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzButtonModule,
    NzToolTipModule,
    RappiderJsonViewerComponent,
    FormsModule,
    RappiderMarkdownViewerModule,
    RappiderHtmlViewerModule,
    NzSpinModule,
    RappiderComponentDefinitionInfoFilesComponent,
    RappiderModalModule
  ],
  templateUrl: './compiled-component-renderer.component.html',
  styleUrls: ['./compiled-component-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompiledComponentRendererComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  /** A reference to the DOM element that will host the rendered component. */
  @ViewChild('bundleContainer', { static: true }) container!: ElementRef;

  // --- Inputs ---
  /** Determines the type of component to render ('angular' or 'flutter'). */
  @Input() componentType: 'angular' | 'flutter' = 'angular';
  /** The definition object for the component, containing metadata like ID or bundle URL. */
  @Input() componentDefinition: any;
  /** The input data to pass to the rendered component. */
  @Input() componentInputs: any;
  /** A trigger to force a re-render of the component when its value changes. */
  @Input() renderBundleTrigger = '';
  /** A flag to control the visibility of the component manifest / definition modal. */
  @Input() displayComponentManifest = false;
  /** The component entity data, used for AI updates and saving. */
  @Input() component?: any;
  /** If true, disables editing features like the AI and data display buttons. */
  @Input() viewOnly? = false;
  /** If true, hides all action buttons (AI, data display, render buttons). */
  @Input() hideActionButtons? = false;
  // if true, shows the input data viewer button
  @Input() allowDisplayingInputData? = true;
  // if true, shows the button to update inputs using AI
  @Input() allowDisplayingInputUpdateUsingAI? = true;
  // if true, shows the button to re-render the component
  @Input() allowReRendering? = true;
  // if true, shows the button to view/edit the component source code
  @Input() allowSourceCodeEditing? = true;

  // --- Public Properties ---
  isComponentInfoModalVisible = false;
  renderError: string | null = null;
  showAIDialog = false;
  showSaveConfirmation = false;
  aiComponentConfigurationInProgress = false;
  newConfigurationExplanation = '';
  aiConfigPrompt = '';
  newComponentDataConfig: any | undefined;
  newComponentDataConfigSummary: any | undefined;
  originalInputs: any | undefined;
  remoteElementRef: any;
  // displayData flag to control input data viewer visibility
  _displayData = false;
  // Flag to control the visibility of the component source code modal
  displayComponentSourceCodeModal = false;

  // Loading states
  isRendering = false;
  isBundleLoading = false;

  private isViewInitialized = false;
  /** UPDATED: Stores the unique function name for this component instance for cleanup. */
  private flutterInputProviderFnName: string | null = null;
  /** Subject for managing subscription cleanup */
  private destroy$ = new Subject<void>();
  /** Store current Flutter engine instance for cleanup */
  private currentFlutterEngine: any = null;
  /** Flag to prevent multiple simultaneous renders */
  private isCurrentlyRendering = false;
  /** Queue for pending render operations */
  private renderQueue: Array<() => void> = [];

  constructor(
    private http: HttpClient,
    private renderer: Renderer2,
    private el: ElementRef,
    private componentGeneratorService: ComponentGeneratorService,
    private uiComponentDataProviderService: UIComponentDataProviderService,
    private componentControllerService: ComponentControllerService,
    private componentDefinitionControllerService: ComponentDefinitionControllerService,
    private notificationService: NotificationService,
    private changeDetectorRef: ChangeDetectorRef,
    private entityFetcherService: EntityFetcherService
  ) {
    // Dependencies are injected via constructor parameters
  }

  // --- Event Listeners ---
  @HostListener('document:flutter-event', ['$event'])
  onFlutterEvent(event: CustomEvent) {
    console.log('Event received from Flutter in Angular!', event);
    const data = JSON.parse(event.detail);
    console.log('Event Type:', data.type);
    console.log('Payload:', data.payload);
    alert(`Received '${data.type}' event from Flutter with title: ${data.payload.projectTitle}`);
  }

  @HostListener('document:flutter-resize', ['$event'])
  onFlutterResize(event: CustomEvent) {
    try {
      const data = JSON.parse(event.detail);
      if (data.type === 'component-resize' && data.payload) {
        const { height, width } = data.payload;
        if (height) {
          this.renderer.setStyle(this.container.nativeElement, 'height', `${height}px`);
        }
        if (width) {
          this.renderer.setStyle(this.container.nativeElement, 'width', `${width}px`);
        }
        console.log(`Flutter component dynamically set container size to: ${width}px x ${height}px`);
      }
    } catch (e) {
      console.error('Error parsing flutter-resize event detail:', e);
    }
  }


  // --- Lifecycle Hooks ---
  ngOnInit(): void {
    // Initialize component state
    this.renderError = null;

    this.getComponentDefinition();

    // Auto-detect component type from framework
    if (this.componentDefinition?.framework) {
      this.componentType = this.componentDefinition.framework;
    } else if (!this.componentType && this.componentDefinition?.supportedFrameworks?.length) {
      if (this.componentDefinition?.supportedFrameworks.includes('angular')) {
        this.componentType = 'angular';
      } else {
        this.componentType = 'flutter';
      }
    }
  }

  /**
   * Get appropriate loading message based on component type
   */
  getLoadingMessage(): string {
    if (this.isBundleLoading) {
      return '';
    }
    return 'Initializing component...';
  }

  /**
   * Clear loading state and process render queue
   */
  private clearLoadingState(): void {
    this.isRendering = false;
    this.isBundleLoading = false;
    this.isCurrentlyRendering = false;
    this.changeDetectorRef.detectChanges();
    this.processRenderQueue();
  }

  /**
   * Show container with proper visibility
   */
  private showContainer(): void {
    if (this.container?.nativeElement) {
      this.renderer.setStyle(this.container.nativeElement, 'display', 'block');
      this.renderer.setStyle(this.container.nativeElement, 'visibility', 'visible');
      this.renderer.setStyle(this.container.nativeElement, 'opacity', '1');
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.renderBundle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isViewInitialized) {
      return;
    }

    // Handle componentInputs changes - only re-render if not first change
    if (changes['componentInputs'] && !changes['componentInputs']?.firstChange) {
      // If component is already rendered, just update inputs instead of full re-render
      if (this.remoteElementRef && this.componentType === 'angular') {
        const inputs = { ...this.componentDefinition?.defaultInputs ?? {}, ...this.componentInputs ?? {} };
        const typedInputs = this.convertInputsToTypedInputs(inputs, this.componentDefinition);
        this.updateInputs(typedInputs);
        return;
      } else {
        // For Flutter or when no remote element exists, do full re-render
        this.renderBundle();
      }
    }

    // Handle renderBundleTrigger changes
    if (changes['renderBundleTrigger'] && !changes['renderBundleTrigger']?.firstChange) {
      this.renderBundle();
    }

    // Only re-render if componentDefinition ID actually changed
    if (changes['componentDefinition'] && !changes['componentDefinition']?.firstChange) {
      const oldId = changes['componentDefinition'].previousValue?.id;
      const newId = changes['componentDefinition'].currentValue?.id;
      if (oldId !== newId) {
        this.renderBundle();
      }
    }
  }

  /** UPDATED: Cleanup the global input provider function when the component is destroyed. */
  ngOnDestroy(): void {
    // Clear render queue
    this.renderQueue = [];
    this.isCurrentlyRendering = false;

    // Complete destroy subject to cancel all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up Flutter input provider function
    if (this.flutterInputProviderFnName && (window as any)[this.flutterInputProviderFnName]) {
      delete (window as any)[this.flutterInputProviderFnName];
    }

    // Clean up Flutter engine if exists
    if (this.currentFlutterEngine) {
      try {
        if (typeof this.currentFlutterEngine.destroy === 'function') {
          this.currentFlutterEngine.destroy();
        } else if (typeof this.currentFlutterEngine.dispose === 'function') {
          this.currentFlutterEngine.dispose();
        } else if (typeof this.currentFlutterEngine.close === 'function') {
          this.currentFlutterEngine.close();
        }
        this.currentFlutterEngine = null;
      } catch (error) {
        console.warn('Error destroying Flutter engine:', error);
      }
    }

    // Clean up container
    if (this.container?.nativeElement) {
      this.container.nativeElement.innerHTML = '';
    }
  }

  // --- Rendering Logic ---
  renderBundle() {
    if (!this.isViewInitialized || !this.componentDefinition) {
      return;
    }

    // Add to render queue to prevent race conditions
    this.renderQueue.push(() => this.executeRender());
    this.processRenderQueue();
  }

  /**
   * Check if component ID indicates a step preview
   * @param componentId - Component ID to check
   * @returns True if component ID starts with 'step_preview_'
   */
  private isStepPreview(componentId: string): boolean {
    return componentId?.startsWith('step_preview_') ?? false;
  }

  /**
   * Check if component ID indicates a branch preview
   * @param componentId - Component ID to check
   * @returns True if component ID starts with 'branch_preview_'
   */
  private isBranchPreview(componentId: string): boolean {
    return componentId?.startsWith('branch_preview_') ?? false;
  }

  /**
 * Check if component ID indicates a version preview
 * @param componentId - Component ID to check
 * @returns True if component ID starts with 'version_preview_'
 */
  private isVersionPreview(componentId: string): boolean {
    return componentId?.startsWith('version_preview_') ?? false;
  }

  /**
   * Extract step ID from component ID
   * @param componentId - Component ID with 'step_preview_' prefix
   * @returns Step ID without prefix
   */
  private extractStepId(componentId: string): string | null {
    if (!this.isStepPreview(componentId)) {
      return null;
    }
    return componentId.replace('step_preview_', '');
  }

  /**
   * Extract branch ID from component ID
   * @param componentId - Component ID with 'branch_preview_' prefix
   * @returns Branch ID without prefix
   */
  private extractBranchId(componentId: string): string | null {
    if (!this.isBranchPreview(componentId)) {
      return null;
    }
    return componentId.replace('branch_preview_', '');
  }

  /**
 * Extract version ID from component ID
 * @param componentId - Component ID with 'version_preview_' prefix
 * @returns Version ID without prefix
 */
  private extractVersionId(componentId: string): string | null {
    if (!this.isVersionPreview(componentId)) {
      return null;
    }
    return componentId.replace('version_preview_', '');
  }

  /**
   * Get latest step ID from branch
   * @param branchId - Unique identifier of the branch
   * @returns Observable with latest step ID or null
   */
  private getLatestStepId(branchId: string): Observable<string | null> {
    return this.componentGeneratorService.getBranchSteps(branchId).pipe(
      takeUntil(this.destroy$),
      switchMap(steps => {
        if (!steps || steps.length === 0) {
          return of(null);
        }

        const sortedSteps = [...steps].sort((a, b) => {
          const dateA = new Date(a.createdDate || a.createdAt || 0).getTime();
          const dateB = new Date(b.createdDate || b.createdAt || 0).getTime();
          return dateB - dateA;
        });

        const latestStep = sortedSteps[0];
        return of(latestStep?.id || null);
      }),
      catchError(error => {
        console.error('Error fetching branch steps:', error);
        return of(null);
      })
    );
  }

  /**
   * Process render queue to ensure only one render at a time
   */
  private processRenderQueue(): void {
    if (this.isCurrentlyRendering || this.renderQueue.length === 0) {
      return;
    }

    const renderFunction = this.renderQueue.shift();
    if (renderFunction) {
      this.isCurrentlyRendering = true;
      renderFunction();
    }
  }

  /**
   * Execute the actual render process
   */
  private executeRender(): void {
    // Immediately hide container to prevent showing old component
    if (this.container?.nativeElement) {
      this.renderer.setStyle(this.container.nativeElement, 'display', 'none');
      this.renderer.setStyle(this.container.nativeElement, 'visibility', 'hidden');
      this.renderer.setStyle(this.container.nativeElement, 'opacity', '0');
    }

    this.isRendering = true;
    this.isBundleLoading = true;
    this.renderError = null;
    this.changeDetectorRef.detectChanges();

    this.cleanupPreviousComponent();

    const componentId = this.component?.id;
    const isStepPreview = componentId && this.isStepPreview(componentId);
    const isBranchPreview = componentId && this.isBranchPreview(componentId);
    const isVersionPreview = componentId && this.isVersionPreview(componentId);

    if (isStepPreview) {
      const stepId = this.extractStepId(componentId);
      if (!stepId) {
        this.renderError = 'Invalid step preview ID format';
        this.clearLoadingState();
        return;
      }

      // Get bundle content for the specific step
      if (this.componentType === 'flutter') {
        this.componentGeneratorService.getBranchBundleContent(
          this.componentDefinition.id,
          stepId
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          bundleContent => {
            if (bundleContent && bundleContent.trim().length > 0) {
              this.renderFlutterBundle(bundleContent);
            } else {
              this.renderError = 'Step bundle content is empty';
              this.clearLoadingState();
            }
          },
          error => {
            console.error('Error fetching step bundle:', error);
            this.renderError = 'Failed to load step bundle';
            this.clearLoadingState();
          }
        );
      }
    } else if (isBranchPreview) {
      // Handle branch preview - get latest step and use its bundle
      const branchId = this.extractBranchId(componentId);
      if (!branchId) {
        this.renderError = 'Invalid branch preview ID format';
        this.clearLoadingState();
        return;
      }

      this.getLatestStepId(branchId).pipe(
        takeUntil(this.destroy$),
        switchMap(stepId => {
          if (!stepId) {
            this.renderError = 'No steps found for this branch';
            this.clearLoadingState();
            return of(null);
          }

          // Get bundle content for the step
          if (this.componentType === 'flutter') {
            return this.componentGeneratorService.getBranchBundleContent(
              this.componentDefinition.id,
              stepId
            ).pipe(
              switchMap(bundleContent => {
                if (bundleContent && bundleContent.trim().length > 0) {
                  this.renderFlutterBundle(bundleContent);
                } else {
                  this.renderError = 'Branch bundle content is empty';
                  this.clearLoadingState();
                }
                return of(null);
              }),
              catchError(error => {
                console.error('Error fetching branch bundle:', error);
                this.renderError = 'Failed to load branch bundle';
                this.clearLoadingState();
                return of(null);
              })
            );
          }
        })
      ).subscribe();
    } else if (isVersionPreview) {
      const versionId = this.extractVersionId(componentId);
      console.log(versionId)
      if (!versionId) {
        this.renderError = 'Invalid version preview ID format';
        this.clearLoadingState();
        return;
      }

      // Get bundle content for the specific version
      if (this.componentType === 'flutter') {
        this.componentGeneratorService.getMainBundleContent(
          this.componentDefinition.id,
          versionId
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          versionContent => {
            if (versionContent && versionContent.trim().length > 0) {
              this.renderFlutterBundle(versionContent);
            } else {
              this.renderError = 'Version bundle content is empty';
              this.clearLoadingState();
            }
          },
          error => {
            console.error('Error fetching version bundle:', error);
            this.renderError = 'Failed to load version bundle';
            this.clearLoadingState();
          }
        );
      }
    } else {
      // Use main bundle for regular components
      if (this.componentType === 'flutter') {
        this.componentGeneratorService.getMainBundleContent(
          this.componentDefinition.id
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          (compiledBundleJsContent) => {
            this.renderFlutterBundle(compiledBundleJsContent);
          },
          error => {
            console.error('Error fetching main bundle:', error);
            this.renderError = 'Failed to load main bundle';
            this.clearLoadingState();
          }
        );
      } else {
        this.renderAngularBundle(this.componentDefinition, this.componentInputs);
      }
    }
  }

  /**
   * Cleans up the previous component instance completely
   */
  private cleanupPreviousComponent(): void {
    if (this.container?.nativeElement) {
      this.container.nativeElement.innerHTML = '';

      // For Flutter components, also remove any Flutter-specific attributes
      this.renderer.removeAttribute(this.container.nativeElement, 'data-input-provider');
      this.renderer.removeAttribute(this.container.nativeElement, 'component-id');
    }

    // Clean up Angular component reference
    this.remoteElementRef = null;

    // Clean up Flutter component if it exists
    if (this.componentType === 'flutter' && this.flutterInputProviderFnName) {
      // Remove the global input provider function
      if ((window as any)[this.flutterInputProviderFnName]) {
        delete (window as any)[this.flutterInputProviderFnName];
      }
      this.flutterInputProviderFnName = null;
    }

    // Clean up previous Flutter engine
    if (this.currentFlutterEngine) {
      try {
        // Check if destroy method exists before calling
        if (typeof this.currentFlutterEngine.destroy === 'function') {
          this.currentFlutterEngine.destroy();
        } else if (typeof this.currentFlutterEngine.dispose === 'function') {
          this.currentFlutterEngine.dispose();
        } else if (typeof this.currentFlutterEngine.close === 'function') {
          this.currentFlutterEngine.close();
        }
        this.currentFlutterEngine = null;
      } catch (error) {
        console.warn('Error destroying Flutter engine:', error);
      }
    }
  }

  /**
   * UPDATED: Renders a Flutter component using an instance-safe data provider function.
   */
  renderFlutterBundle(bundleContent?: string): void {
    const bundleUrlFromApi = this.componentDefinition?.bundleUrl ?? this.getBundleUrl();

    const assetBase = '/assets/scripts/flutter/';

    // Container is already hidden in renderBundle method

    // 1. Create a unique function name for this component instance.
    this.flutterInputProviderFnName = `input_${this.component.id}`;

    // 2. Create a function on the window object with that unique name.
    // This function closes over the current component's inputs.
    (window as any)[this.flutterInputProviderFnName] = () =>
      this.componentInputs ? JSON.stringify(this.componentInputs) : null;

    // 3. Set a data attribute on the host element so the Flutter app knows which function to call.
    this.renderer.setAttribute(this.container.nativeElement, 'data-input-provider', this.flutterInputProviderFnName);
    this.renderer.setAttribute(this.container.nativeElement, 'component-id', this.component.id);

    // Check if we already have the bundle content
    if (bundleContent && bundleContent.trim().length > 0) {
      // Use the existing bundle content
      const blob = new Blob([bundleContent], { type: 'application/javascript' });
      const blobUrl = URL.createObjectURL(blob);

      _flutter.loader.loadEntrypoint({
        entrypointUrl: blobUrl,
        onEntrypointLoaded: async (engineInitializer: any) => {
          try {
            const appRunner = await engineInitializer.initializeEngine({
              hostElement: this.container.nativeElement,
              assetBase: assetBase,
            });

            // Store engine reference for cleanup
            this.currentFlutterEngine = appRunner;

            await appRunner.runApp();

            if (this.container?.nativeElement) {
              this.renderer.setStyle(this.container.nativeElement, 'display', 'block');
              this.renderer.setStyle(this.container.nativeElement, 'visibility', 'visible');
              this.renderer.setStyle(this.container.nativeElement, 'opacity', '1');
            }
            this.isRendering = false;
            this.isBundleLoading = false;
            this.isCurrentlyRendering = false;
            this.changeDetectorRef.detectChanges();

            // Process next render in queue
            this.processRenderQueue();
          } catch (error) {
            console.error('Error initializing Flutter component:', error);
            this.renderError = `Error initializing Flutter component: ${error.message}`;
            if (this.container?.nativeElement) {
              this.renderer.setStyle(this.container.nativeElement, 'display', 'block');
            }
            this.isRendering = false;
            this.isBundleLoading = false;
            this.isCurrentlyRendering = false;
            this.changeDetectorRef.detectChanges();

            // Process next render in queue
            this.processRenderQueue();
          } finally {
            URL.revokeObjectURL(blobUrl);
          }
        }
      });
    } else if (bundleUrlFromApi) {
      // Fallback to fetching from API if no bundle content is available
      this.http.get(bundleUrlFromApi, { responseType: 'text' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (jsContent) => {
            const blob = new Blob([jsContent], { type: 'application/javascript' });
            const blobUrl = URL.createObjectURL(blob);

            _flutter.loader.loadEntrypoint({
              entrypointUrl: blobUrl,
              onEntrypointLoaded: async (engineInitializer: any) => {
                try {
                  const appRunner = await engineInitializer.initializeEngine({
                    hostElement: this.container.nativeElement,
                    assetBase: assetBase,
                  });

                  // Store engine reference for cleanup
                  this.currentFlutterEngine = appRunner;

                  await appRunner.runApp();

                  // Show container and clear loading state
                  this.showContainer();
                  this.clearLoadingState();
                } catch (error) {
                  console.error('Error initializing Flutter component:', error);
                  this.renderError = `Error initializing Flutter component: ${error.message}`;
                  // Show container even on error
                  this.showContainer();
                  this.clearLoadingState();
                } finally {
                  URL.revokeObjectURL(blobUrl);
                }
              }
            });
          },
          error: (err) => {
            console.error('Failed to fetch Flutter bundle from API:', err);
            this.renderError = `Failed to fetch Flutter bundle from ${bundleUrlFromApi}. Check CORS policy and API availability.`;
            // Show container even on error
            this.showContainer();
            this.clearLoadingState();
          }
        });
    } else {
      this.renderError = 'No bundle URL or content available for Flutter component.';
      // Show container even on error
      this.showContainer();
      this.clearLoadingState();
    }
  }

  // --- (All other methods remain the same) ---
  renderAngularBundle(componentDefinition: any, componentInputs: any) {
    console.log('RenderAngularBundle: Starting', { componentDefinition, componentInputs });

    if (!componentDefinition?.id) {
      console.error('RenderAngularBundle: No component definition ID');
      this.renderError = 'Component definition ID is missing for Angular component.';
      this.clearLoadingState();
      return;
    }

    this.cleanupExistingScripts();
    this.componentGeneratorService.getCompiledComponentBundleContent(componentDefinition.id, 'angular').subscribe(
      compiledBundleJsContent => {
        console.log('RenderAngularBundle: Bundle content received', {
          contentLength: compiledBundleJsContent?.length,
          componentId: componentDefinition.id
        });

        const blob = new Blob([compiledBundleJsContent], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        const script = this.renderer.createElement('script');
        script.type = 'module';
        script.src = blobUrl;

        script.onload = () => {
          console.log('RenderAngularBundle: Script loaded successfully');
          try {
            const tagName = componentDefinition?.selector;
            console.log('RenderAngularBundle: Creating element with tagName', tagName);

            if (tagName) {
              const remoteElement = this.renderer.createElement(tagName);
              this.remoteElementRef = remoteElement;
              const inputs = { ...componentDefinition?.defaultInputs ?? {}, ...componentInputs ?? {} };
              const typedInputs = this.convertInputsToTypedInputs(inputs, componentDefinition);
              this.updateInputs(typedInputs);
              this.renderer.appendChild(this.container.nativeElement, remoteElement);
              console.log('RenderAngularBundle: Element appended to container');
            }

            console.log('RenderAngularBundle: Clearing loading state');
            this.clearLoadingState();
            this.showContainer();
          } catch (error) {
            console.error('Error creating Angular component:', error);
            this.renderError = `Error creating component: ${error.message}`;
            this.clearLoadingState();
          } finally {
            URL.revokeObjectURL(blobUrl);
          }
        };

        script.onerror = (e: any) => {
          console.error('RenderAngularBundle: Script load error', e);
          this.renderError = `Error loading module: ${e.message}`;
          this.isRendering = false;
          this.isBundleLoading = false;
          this.changeDetectorRef.detectChanges();
          URL.revokeObjectURL(blobUrl);
        };

        this.renderer.appendChild(document.body, script);
      },
      error => {
        console.error('RenderAngularBundle: Error fetching bundle content', error);
        this.renderError = `Error fetching bundle content: ${error.message || error}`;
        this.clearLoadingState();
      }
    );
  }

  getBundleUrl(): string | null {
    let bundleUrl = null;

    if (this.componentDefinition?.bundleUrl) {
      bundleUrl = this.componentDefinition.bundleUrl;
    } else if (this.componentDefinition?.id) {
      bundleUrl = this.componentGeneratorService.getMainBundleUrl(
        this.componentDefinition.id
      );
    }
    return bundleUrl;
  }

  /**
   * Cleans up existing script elements to prevent memory leaks
   */
  private cleanupExistingScripts(): void {
    const existingScripts = document.querySelectorAll('script[type="module"]');
    existingScripts.forEach(script => {
      const scriptElement = script as HTMLScriptElement;
      if (scriptElement.src && scriptElement.src.includes('blob:')) {
        script.remove();
      }
    });
  }

  updateInputs(updatedInputs: any) {
    if (this.remoteElementRef) {
      Object.keys(updatedInputs).forEach(key => {
        this.remoteElementRef[key] = updatedInputs[key];
      });
    }
  }

  convertInputsToTypedInputs(inputs: any, componentDefinition: any): any {
    if (!componentDefinition?.inputDefinitions?.length || !inputs) {
      return inputs;
    }
    const typedInputs: any = {};
    Object.keys(inputs).forEach(key => {
      const inputDefinition = componentDefinition?.inputDefinitions?.find((def: any) => def.fieldName === key);
      if (!inputDefinition) {
        typedInputs[key] = inputs[key];
      } else {
        if (inputDefinition?.type?.type === 'primitive' ||
          (environment.primitiveDataSchemaId && inputDefinition?.typeId === environment.primitiveDataSchemaId)) {
          typedInputs[key] = inputs[key];
        } else {
          if (inputs[key] && typeof inputs[key] === 'string') {
            if (inputs[key]?.startsWith('{') || inputs[key].startsWith('[')) {
              try {
                typedInputs[key] = JSON.parse(inputs[key]);
              } catch (e) {
                typedInputs[key] = inputs[key];
              }
            } else {
              typedInputs[key] = inputs[key];
            }
          } else {
            typedInputs[key] = inputs[key];
          }
        }
      }
    });
    return typedInputs;
  }

  showBundleInfoModal() {
    this.displayComponentManifest = !this.displayComponentManifest;
  }
  toggleDisplayData() {
    this._displayData = !this._displayData;
  }
  displayAIConfigDialog() {
    this.showAIDialog = true;
  }

  updateComponentConfigUsingAI() {
    console.log(this.aiConfigPrompt);
    this.showSaveConfirmation = false;
    this.aiComponentConfigurationInProgress = true;
    this.changeDetectorRef.detectChanges();
    const entityName = 'SensorData';
    const inputDataProviderType = 'entity';
    this.uiComponentDataProviderService.getComponentConfigUsingAi(
      this.aiConfigPrompt, entityName)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: { componentInputDataConfig: any; componentInputData: any }) => {
          if (response?.componentInputDataConfig && response?.componentInputData &&
            !lodash.isEmpty(response?.componentInputData)) {
            this.newComponentDataConfig = {
              type: inputDataProviderType,
              entityName: entityName,
              config: response?.componentInputDataConfig,
              sampleData: response?.componentInputData
            };
            this.newComponentDataConfigSummary = {
              type: 'Data Source',
              entityName: entityName,
              ...response?.componentInputDataConfig
            };
            delete this.newComponentDataConfigSummary.filter;
            delete this.newComponentDataConfigSummary.data;
            delete this.newComponentDataConfigSummary.transformationFunction;
          }
          if (response?.componentInputData) {
            this.originalInputs = { ...this.componentInputs };
            this.componentInputs = { ...this.componentInputs, ...response.componentInputData };
            this.renderBundle();
          }
          this.showSaveConfirmation = true;
          this.aiComponentConfigurationInProgress = false;
          this.changeDetectorRef.detectChanges();
        },
        error => {
          console.error('Error fetching component config from AI:', error);
          this.aiComponentConfigurationInProgress = false;
          this.changeDetectorRef.detectChanges();
          this.notificationService.createNotification(
            'error', 'Error', 'Failed to fetch component configuration from AI.');
        }
      );
  }

  onConfirmSaveComponentDataConfig() {
    this.saveComponentDataConfig();
    this.showSaveConfirmation = false;
    this.aiComponentConfigurationInProgress = false;
    this.newConfigurationExplanation = '';
    this.changeDetectorRef.detectChanges();
  }

  saveComponentDataConfig() {
    const patchBody = {
      inputDataProviderConfig: this.newComponentDataConfig,
      inputs: {
        ...this.componentInputs,
        ...(this.newComponentDataConfig?.sampleData || {})
      }
    };
    this.componentControllerService.updateById({ id: this.component?.id, body: patchBody })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          console.log('Component data config updated successfully:', response);
          this.showAIDialog = false;
          this.notificationService.createNotification(
            'success', 'Success', 'The component configuration has been successfully updated.');
          this.changeDetectorRef.detectChanges();
        },
        error => {
          console.error('Error updating component data config:', error);
          this.notificationService.createNotification('error', 'Error', 'Failed to update component configuration.');
          this.showAIDialog = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  displayComponentSourceCode() {
    this.displayComponentSourceCodeModal = true;
  }

  getComponentDefinition() {
    this.entityFetcherService.findItemById(
      this.componentDefinition.id,
      selectComponentDefinitionById,
      (params: any) => this.componentDefinitionControllerService.find(params),
      {},
      (fetchedComponentDefinition: any) => new UpdateComponentDefinitionSuccessful({
        componentDefinitionId: this.componentDefinition.id,
        updatedComponentDefinition: fetchedComponentDefinition,
        navigateToDetailPage: false
      })
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (componentDefinition) => {
        if (componentDefinition) {
          this.componentDefinition = componentDefinition;
          this.renderBundle();
        }
      },
      (error) => {
        console.error('Error fetching component definition:', error);
      }
    );
  }

  discardAiConfigChanges() {
    if (this.originalInputs) {
      this.updateInputs(this.originalInputs);
      this.componentInputs = this.originalInputs;
      this.renderBundle();
    }
    this.showSaveConfirmation = false;
    this.newConfigurationExplanation = '';
    this.showAIDialog = false;
    this.changeDetectorRef.detectChanges();
  }
}
